import React, { HTMLAttributes, ReactNode } from 'react'
import { Draggable, DraggableProvided } from 'react-beautiful-dnd'
import classNames from 'classnames'
import DragHandleIcon from 'transfix-icons/build/DragHandleIcon'
import Box from 'transfix-ui/core/Box'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { shadows } from 'transfix-ui/theme/shadows'

export interface IDraggableListItemProps extends HTMLAttributes<HTMLDivElement> {
  index: number
  draggableId: string
  children: ReactNode
  /**
   * Hides draggable icon on left
   * @default false
   */
  hideIcon?: boolean
  /**
   * Disables draggable
   * @default false
   */
  isDragDisabled?: boolean
}

const useDraggableListItemStyles = makeStyles(() => ({
  root: {
    width: '100%',
    background: brandColors.white,
    display: 'flex',
    alignItems: 'center',
    borderTop: `1px solid ${brandColors.coolGray2}`,
    borderBottom: `1px solid ${brandColors.coolGray2}`,
    marginTop: '-1px',
  },
  isDragging: {
    boxShadow: shadows[4],
  },
  iconHidden: {
    opacity: 0,
  },
}))

const getCombinedProps = (
  props: Omit<IDraggableListItemProps, 'children' | 'draggableId' | 'index'>,
  draggableProvided: Omit<DraggableProvided, 'innerRef'>
) => {
  return {
    ...props,
    ...draggableProvided.draggableProps,
    ...draggableProvided.dragHandleProps,
  }
}

export const DraggableListItem = ({
  children,
  draggableId,
  hideIcon = false,
  index,
  isDragDisabled = false,
  ...rest
}: IDraggableListItemProps) => {
  const classes = useDraggableListItemStyles()
  return (
    <Draggable draggableId={draggableId} index={index} isDragDisabled={isDragDisabled}>
      {({ innerRef, ...draggableProvided }, draggableSnapshot) => {
        const props = getCombinedProps(rest, draggableProvided)
        const className = classNames(classes.root, {
          [classes.isDragging]: draggableSnapshot.isDragging,
        })
        const iconClassName = classNames({
          [classes.iconHidden]: hideIcon,
        })

        return (
          <div data-test='DraggableListItem' className={className} ref={innerRef} {...props}>
            <Box flex='0 0 auto' p={2}>
              <DragHandleIcon size='large' color='coolGray5' className={iconClassName} />
            </Box>
            <Box flex='1 1 auto' p={2}>
              {children}
            </Box>
          </div>
        )
      }}
    </Draggable>
  )
}
